import React, { useState } from 'react';
import { Check, Calendar, Phone, Mail, User, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

// Opciones de prendas con iconos
const PRENDAS_OPCIONES = [
  { id: 'traje', label: 'Traje', emoji: '🤵' },
  { id: 'smoking', label: 'Smoking', emoji: '🎩' },
  { id: 'blazer', label: 'Blazer', emoji: '🧥' },
  { id: 'pantalon', label: 'Pantalón', emoji: '👖' },
  { id: 'camisa', label: 'Camisa', emoji: '👔' },
  { id: 'chaleco', label: 'Chaleco', emoji: '🦺' },
  { id: 'zapatos', label: 'Zapatos', emoji: '👞' },
  { id: 'tenis', label: 'Tenis', emoji: '👟' },
  { id: 'traje_novio', label: 'Traje de novio', emoji: '💒' },
  { id: 'alquiler', label: 'Alquiler', emoji: '🏷️' },
  { id: 'otro', label: 'Otro', emoji: '✨' },
];

const AutoregistroCliente = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    correo: '',
    telefono: '',
    telefonoConfirm: '',
    fechaEvento: '',
    sinFechaDefinida: false,
    prendas: [],
    prendaOtro: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Validar email
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Manejar cambio en campos
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Manejar selección de prendas
  const togglePrenda = (prendaId) => {
    setFormData(prev => {
      const nuevasPrendas = prev.prendas.includes(prendaId)
        ? prev.prendas.filter(p => p !== prendaId)
        : [...prev.prendas, prendaId];
      return { ...prev, prendas: nuevasPrendas };
    });
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es obligatorio';
    }

    if (!formData.correo.trim()) {
      newErrors.correo = 'El correo es obligatorio';
    } else if (!isValidEmail(formData.correo)) {
      newErrors.correo = 'Ingresa un correo válido';
    }

    if (!formData.telefono.trim()) {
      newErrors.telefono = 'El teléfono es obligatorio';
    }

    if (!formData.telefonoConfirm.trim()) {
      newErrors.telefonoConfirm = 'Confirma tu número de teléfono';
    } else if (formData.telefono !== formData.telefonoConfirm) {
      newErrors.telefonoConfirm = 'Los números de teléfono no coinciden. Por favor verifica la información.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const payload = {
        nombre: formData.nombre.trim(),
        correo: formData.correo.trim().toLowerCase(),
        telefono: formData.telefono.trim(),
        fecha_evento: formData.sinFechaDefinida ? 'sin_fecha' : formData.fechaEvento || null,
        prendas: formData.prendas,
        prenda_otro: formData.prendas.includes('otro') ? formData.prendaOtro : null,
        origen_formulario: 'web'
      };

      const response = await fetch(`${API_URL}/api/public/autorregistro`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitSuccess(true);
      } else {
        setSubmitError(data.detail || 'Ocurrió un error. Por favor intenta de nuevo.');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitError('Error de conexión. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Pantalla de confirmación
  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-semibold text-white mb-4">
              ¡Gracias!
            </h1>
            <p className="text-zinc-400 mb-2">
              Hemos recibido tus datos.
            </p>
            <p className="text-zinc-400 mb-6">
              Nuestro equipo de <span className="text-white font-medium">Juan Pablo Murillo</span> se pondrá en contacto contigo para continuar con tu asesoría.
            </p>
            <div className="pt-4 border-t border-zinc-800">
              <p className="text-zinc-500 text-sm">
                Muy pronto tendrás noticias nuestras
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 py-6">
        <div className="max-w-lg mx-auto px-4 text-center">
          <h1 className="text-2xl font-light tracking-widest text-white uppercase">
            Juan Pablo Murillo
          </h1>
          <p className="text-zinc-500 text-sm mt-1 tracking-wider">
            Sastrería & Moda Masculina
          </p>
        </div>
      </header>

      {/* Formulario */}
      <main className="max-w-lg mx-auto px-4 py-8">
        <div className="bg-zinc-900 rounded-2xl p-6 md:p-8 border border-zinc-800">
          <div className="text-center mb-8">
            <h2 className="text-xl font-medium text-white mb-2">
              Aquí empieza tu traje
            </h2>
            <p className="text-zinc-400 text-sm">
              Déjanos tus datos y te contactaremos para comenzar tu asesoría personalizada
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <User className="w-4 h-4 inline mr-2" />
                Nombre completo *
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => handleChange('nombre', e.target.value)}
                className={`w-full px-4 py-3 bg-zinc-800 border ${errors.nombre ? 'border-red-500' : 'border-zinc-700'} rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500 transition-colors`}
                placeholder="Tu nombre completo"
                data-testid="input-nombre"
              />
              {errors.nombre && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> {errors.nombre}
                </p>
              )}
            </div>

            {/* Correo */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Correo electrónico *
              </label>
              <input
                type="email"
                value={formData.correo}
                onChange={(e) => handleChange('correo', e.target.value)}
                className={`w-full px-4 py-3 bg-zinc-800 border ${errors.correo ? 'border-red-500' : 'border-zinc-700'} rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500 transition-colors`}
                placeholder="tu@correo.com"
                data-testid="input-correo"
              />
              {errors.correo && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> {errors.correo}
                </p>
              )}
            </div>

            {/* Teléfono */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                Teléfono / WhatsApp *
              </label>
              <input
                type="tel"
                value={formData.telefono}
                onChange={(e) => handleChange('telefono', e.target.value)}
                className={`w-full px-4 py-3 bg-zinc-800 border ${errors.telefono ? 'border-red-500' : 'border-zinc-700'} rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500 transition-colors`}
                placeholder="Tu número completo"
                data-testid="input-telefono"
              />
              {errors.telefono && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> {errors.telefono}
                </p>
              )}
            </div>

            {/* Confirmar Teléfono */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                Confirmar número de teléfono *
              </label>
              <input
                type="tel"
                value={formData.telefonoConfirm}
                onChange={(e) => handleChange('telefonoConfirm', e.target.value)}
                className={`w-full px-4 py-3 bg-zinc-800 border ${errors.telefonoConfirm ? 'border-red-500' : 'border-zinc-700'} rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500 transition-colors`}
                placeholder="Escribe nuevamente tu número"
                data-testid="input-telefono-confirm"
              />
              {errors.telefonoConfirm && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> {errors.telefonoConfirm}
                </p>
              )}
              {formData.telefono && formData.telefonoConfirm && formData.telefono === formData.telefonoConfirm && (
                <p className="mt-1 text-sm text-emerald-500 flex items-center gap-1">
                  <Check className="w-4 h-4" /> Los números coinciden
                </p>
              )}
            </div>

            {/* Fecha del evento */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                ¿Para qué fecha necesitas tu prenda?
              </label>
              {!formData.sinFechaDefinida && (
                <input
                  type="date"
                  value={formData.fechaEvento}
                  onChange={(e) => handleChange('fechaEvento', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-zinc-500 transition-colors"
                  data-testid="input-fecha"
                />
              )}
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sinFechaDefinida}
                  onChange={(e) => {
                    handleChange('sinFechaDefinida', e.target.checked);
                    if (e.target.checked) handleChange('fechaEvento', '');
                  }}
                  className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-zinc-900"
                />
                <span className="text-zinc-400 text-sm">Aún no tengo fecha definida</span>
              </label>
            </div>

            {/* Prendas */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-3">
                ¿Qué prenda estás buscando?
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {PRENDAS_OPCIONES.map((prenda) => (
                  <button
                    key={prenda.id}
                    type="button"
                    onClick={() => togglePrenda(prenda.id)}
                    className={`p-3 rounded-xl border transition-all text-center ${
                      formData.prendas.includes(prenda.id)
                        ? 'bg-zinc-700 border-zinc-500 text-white'
                        : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                    }`}
                    data-testid={`btn-prenda-${prenda.id}`}
                  >
                    <span className="text-xl block mb-1">{prenda.emoji}</span>
                    <span className="text-xs">{prenda.label}</span>
                    {formData.prendas.includes(prenda.id) && (
                      <Check className="w-4 h-4 mx-auto mt-1 text-emerald-500" />
                    )}
                  </button>
                ))}
              </div>
              
              {/* Campo "Otro" */}
              {formData.prendas.includes('otro') && (
                <input
                  type="text"
                  value={formData.prendaOtro}
                  onChange={(e) => handleChange('prendaOtro', e.target.value)}
                  className="w-full mt-3 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500 transition-colors"
                  placeholder="¿Cuál prenda buscas?"
                  data-testid="input-prenda-otro"
                />
              )}
            </div>

            {/* Error general */}
            {submitError && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-red-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {submitError}
                </p>
              </div>
            )}

            {/* Botón enviar */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-white text-zinc-900 rounded-xl font-semibold hover:bg-zinc-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="btn-enviar"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Enviando...
                </>
              ) : (
                'ENVIAR MIS DATOS'
              )}
            </button>
          </form>

          <p className="text-center text-zinc-500 text-xs mt-6">
            Tus datos están seguros con nosotros
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-zinc-600 text-xs">
          © {new Date().getFullYear()} Juan Pablo Murillo
        </p>
      </footer>
    </div>
  );
};

export default AutoregistroCliente;
